import { FunctionComponent, useContext, useState } from "react";
import { DeliveryChargeRuleScope, IDeliveryChargeRule } from "./interfaces";
import CustomDialog from "../../../common/custom-dialog/custom-dialog";
import { DialogTitle, DialogContent, TextField, DialogActions, Button, Alert } from "@mui/material";
import { API_ENDPOINT } from "../../../../configurations/global.config";
import { AuthContext } from "../../../../contexts/user-context/user-context";
import Decimal from "decimal.js";
import { MerchantType } from "../../../../interfaces/delivery-company/merchants";
import axios from "axios";
import { useSnackBar } from "../../../../contexts/snackbar";
import ConfirmAction from "../../../common/confirm-action/confirm-action";
import { displayDeliveryRule } from "./utils";
import { DataResources, ResourceActionType, user_has_data_model_access } from "../../../../utils/permissions";

interface DeliveryChargeFormDialogProps {
    open: boolean
    rule: IDeliveryChargeRule
    onUpdateSuccess: () => void
    onDeleteSuccess: () => void
    onClose: () => void
    merchant: MerchantType
}
 
const DeliveryChargeFormDialog: FunctionComponent<DeliveryChargeFormDialogProps> = ({
    open,
    rule, 
    onClose, 
    onUpdateSuccess,
    onDeleteSuccess,
    merchant
}) => {
    const [chargeInput, setChargeInput] = useState("");
    const [isSaving, setIsSaving] = useState(false)
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
    const {config} = useContext(AuthContext)
    const {showMessage} = useSnackBar()
    const { user } = useContext(AuthContext)

    const handleSave = async () => {
        if (isNaN(Number(chargeInput))) return;
        setIsSaving(true)
        try {
            const endpoint = `${API_ENDPOINT}/v1/delivery-fee-rules/`
            const requestData :any = {
                scope: rule.scope,
                delta_charge: new Decimal(chargeInput),            
                merchant: {id: merchant.id},
            }
    
            if (rule.scope == DeliveryChargeRuleScope.AREA) requestData.area = rule.area
            if (rule.scope == DeliveryChargeRuleScope.DISTRICT) requestData.district = rule.district
            if (rule.scope == DeliveryChargeRuleScope.GOVERNORATE) requestData.governorate = rule.governorate 
            await axios.post<IDeliveryChargeRule>(endpoint, requestData, config)
            onUpdateSuccess()
            onClose()
        } catch {
            showMessage('Something went wrong. Try again or contact us.', 'error')
        } finally {
            setIsSaving(false)
        }
      };
    
      const handleDelete = async () => {
        if (rule.id) {
            setIsSaving(true)
            try {
                await axios.delete(`${API_ENDPOINT}/v1/delivery-fee-rules/${rule.id}/`, config)
                onDeleteSuccess()
            } catch {
                showMessage('Failed to remove rule', 'error')
            } finally {
                setIsSaving(false)
            }
        }
      }
    
    return open ? <CustomDialog open={true} onClose={onClose} maxWidth={'sm'} fullWidth>
          <DialogTitle>
            {rule.id ? "Edit Delivery Rule" : "Add Delivery Rule"} for {displayDeliveryRule(rule)}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Extra Charge Amount ($)"
              fullWidth
              margin="dense"
              type="number"
              value={chargeInput}
              required
              onChange={(e) => setChargeInput(e.target.value)}
            />
          </DialogContent>
          <DialogActions sx={{display:"flex", justifyContent:"center", }}>
            <Button
              disabled={isSaving || !chargeInput || (rule.id != undefined && !user_has_data_model_access(user, DataResources.DELIVERY_FEE_RULES, ResourceActionType.UPDATE))}
              variant="contained" onClick={handleSave}>
              Save
            </Button>
            <Button  color="error" variant="outlined" onClick={()=>setIsConfirmingDelete(true)} 
                disabled={isSaving || (rule.id != undefined && !user_has_data_model_access(user, DataResources.DELIVERY_FEE_RULES, ResourceActionType.DELETE))}>
              Delete
            </Button>
          </DialogActions>

          {
            isConfirmingDelete && <ConfirmAction 
                    message={`Are you sure you want to remove the extra charge for ${displayDeliveryRule(rule)}`}
                    action={() => handleDelete()}
                    isOpen={true}
                    handleClose={()=>{setIsConfirmingDelete(false); onClose()}}>
                        <Alert severity="info"> Deleting a rule does not have a retroactive effect; old orders that used this rule will not be impacted.</Alert>
                    </ConfirmAction>
          }
        </CustomDialog> : null
}
 
export default DeliveryChargeFormDialog;